import { useEffect, useRef } from 'react';

import { KeyboardShortcuts } from '../keyboard-shortcuts';
import type { KeyboardShortcutsConfig } from '../types';

/**
 * Options for {@link useKeyboardShortcutsInstance}.
 */
export interface UseKeyboardShortcutsInstanceOptions {
  /** Config passed to the KeyboardShortcuts constructor. */
  config?: KeyboardShortcutsConfig;
  /** Runs once, when the instance is first created (e.g. to enable scopes). */
  configure?: (instance: KeyboardShortcuts) => void;
}

/**
 * Create and own a KeyboardShortcuts instance for this component's lifetime:
 * one instance with `configure` applied, destroyed on unmount.
 */
export function useKeyboardShortcutsInstance(options: UseKeyboardShortcutsInstanceOptions = {}): KeyboardShortcuts {
  const instanceRef = useRef<KeyboardShortcuts | null>(null);

  if (instanceRef.current === null) {
    instanceRef.current = new KeyboardShortcuts(options.config);
    options.configure?.(instanceRef.current);
  }

  useEffect(() => {
    const instance = instanceRef.current;
    return () => {
      instance?.destroy();
      // destroy() is unrecoverable, so a remount must not reuse the instance.
      instanceRef.current = null;
    };
  }, []);

  return instanceRef.current;
}
